import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/base/button";
import { Modal, ModalHeading } from "@/components/base/modal";
import { addToast } from "@/components/base/toast";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateAllTournamentQueries } from "@/lib/tournament-cache";

type ForfeitPoolTeamFormProps = {
  poolId: number;
  teamId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ForfeitPoolTeamForm({
  poolId,
  teamId,
  isOpen,
  onOpenChange,
}: ForfeitPoolTeamFormProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason, isPending } = useMutation({
    ...trpc.tournaments.forfeitPoolTeam.mutationOptions(),
    onSuccess: (data) => {
      invalidateAllTournamentQueries(queryClient, trpc);
      onOpenChange(false);
      addToast({
        variant: "success",
        title: "Team forfeited",
        description: `${data.forfeitedMatches} match${data.forfeitedMatches === 1 ? "" : "es"} marked as forfeit.`,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <ModalHeading>Forfeit Pool Matches</ModalHeading>
        <p>
          Are you sure you want to forfeit this team? All of their pool matches
          — including ones already played — will be overwritten to forfeit
          scores and the opponent will be awarded the win.
        </p>

        {failureReason && (
          <p className="text-sm text-red-600">{failureReason.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="solid"
            color="danger"
            isDisabled={isPending}
            onPress={() => mutate({ poolId, teamId })}
          >
            {isPending ? "Forfeiting..." : "Forfeit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function UnforfeitPoolTeamForm({
  poolId,
  teamId,
  isOpen,
  onOpenChange,
}: ForfeitPoolTeamFormProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason, isPending } = useMutation({
    ...trpc.tournaments.unforfeitPoolTeam.mutationOptions(),
    onSuccess: (data) => {
      invalidateAllTournamentQueries(queryClient, trpc);
      onOpenChange(false);
      addToast({
        variant: "success",
        title: "Forfeit reversed",
        description: `${data.restoredMatches} match${data.restoredMatches === 1 ? "" : "es"} reset to scheduled.`,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <ModalHeading>Undo Forfeit</ModalHeading>
        <p>
          Are you sure you want to undo the forfeit for this team? All forfeited
          matches will be reset to scheduled with scores cleared.
        </p>

        {failureReason && (
          <p className="text-sm text-red-600">{failureReason.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="solid"
            color="primary"
            isDisabled={isPending}
            onPress={() => mutate({ poolId, teamId })}
          >
            {isPending ? "Restoring..." : "Undo Forfeit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
